import { useState } from "react";
import { useT, translateError } from "../i18n/hooks.ts";
import { isSeriesScaleLocked, type DailySeries, type ProjectSeries } from "../model/dailySeries.ts";
import {
  updateAquifer,
  updateAquiferColumn,
  addAquifer,
  deleteAquifer,
} from "../model/projectEntities.ts";
import type { SeriesScale } from "../model/seriesScale.ts";
import type { EvalHidProject } from "../model/types.ts";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog.tsx";
import { Dialog } from "./Dialog.tsx";
import { PasteDailySeries } from "./PasteDailySeries.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

/**
 * Aquifer building blocks shared by the new-project wizard
 * (Step3Outlets.tsx) and the editor's own catalog (AquifersPanel.tsx) —
 * also used directly by SubBasinCanvas.tsx/SubBasinMap.tsx
 * (EditAquiferDialog). Lives directly under ui/ (not wizard/ or editor/)
 * so none of those have to import it from another — see
 * project_evalhid_code_audit.md.
 */

/** Exported: also used by SubBasinCanvas.tsx/SubBasinMap.tsx (aquifers area of the card view). */
export function EditAquiferDialog({
  initial,
  outletPoints,
  pumpingSeries,
  onChangePumpingSeries,
  pumpingCol,
  scale,
  scaleLocked = true,
  onChangePumpingIndex,
  onDeleteSeries,
  onSave,
  onClose,
}: {
  initial: { name: string; vo: number; alfa: number; outletCode: number | null };
  outletPoints: { outletCode: number; name: string }[];
  /** Project's pumping series (null if none loaded yet) — pasting/viewing is only offered if the caller also passes onChangePumpingSeries/pumpingCol/onChangePumpingIndex (e.g. not in the creation wizard, where series don't exist yet). */
  pumpingSeries?: DailySeries | null;
  onChangePumpingSeries?: (series: DailySeries) => void;
  /** Already-persisted (1-based) index of the Pumping column (Aquifer.pumpingCol) — 0: unlinked. */
  pumpingCol?: number;
  /** The project's own scale (EvalHidProject.seriesScale) — absent only alongside the other series props, in the creation wizard where there's no project/series yet. */
  scale?: SeriesScale;
  /** Whether the project's scale is already committed (isSeriesScaleLocked, dailySeries.ts) — see PasteDailySeries.tsx's own prop. Defaults to `true` (no auto-detection) for callers with no real project/series (creation wizard). */
  scaleLocked?: boolean;
  onChangePumpingIndex?: (index: number, inferredScale?: SeriesScale) => void;
  /** Enables "Delete series" (permanent) in the series tab — see PasteDailySeries.tsx/deleteAquiferPumpingColumn (projectEntities.ts). Only passed by the callers with a real project (SubBasinMap.tsx/SubBasinCanvas.tsx), not the creation wizard. */
  onDeleteSeries?: () => { sharedWithOthers: boolean; confirm: () => void };
  onSave: (changes: { name: string; vo: number; alfa: number; outletCode: number | null }) => void;
  onClose: () => void;
}) {
  const hasSeries = !!(onChangePumpingSeries && onChangePumpingIndex);
  const [tab, setTab] = useState<"data" | "series">("data");
  const [name, setName] = useState(initial.name);
  const [vo, setVo] = useState(String(initial.vo));
  const [alfa, setAlfa] = useState(String(initial.alfa));
  const [outletCode, setOutletCode] = useState<string>(
    initial.outletCode != null ? String(initial.outletCode) : ""
  );
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  return (
    <Dialog title={t("step3.editAquiferTitle")} onClose={onClose}>
      {hasSeries && (
        <div className="dialog-tabs">
          <button
            className={tab === "data" ? "tab-active" : undefined}
            onClick={() => setTab("data")}
          >
            {t("editSubBasinDialog.basicData")}
          </button>
          <button
            className={tab === "series" ? "tab-active" : undefined}
            onClick={() => setTab("series")}
          >
            {t("common.series")}
          </button>
        </div>
      )}

      {(!hasSeries || tab === "data") && (
        <div className="step-form">
          <input value={name} onChange={(e) => setName(e.target.value)} />
          <input
            placeholder="Vo"
            type="number"
            value={vo}
            onChange={(e) => setVo(e.target.value)}
          />
          <input
            placeholder="Alfa"
            type="number"
            value={alfa}
            onChange={(e) => setAlfa(e.target.value)}
          />
          <select value={outletCode} onChange={(e) => setOutletCode(e.target.value)}>
            <option value="">{t("common.outletPointPlaceholder")}</option>
            {outletPoints.map((p) => (
              <option key={p.outletCode} value={p.outletCode}>
                {p.name}
              </option>
            ))}
          </select>
          {error && <p className="error">{error}</p>}
        </div>
      )}

      {tab === "series" && onChangePumpingSeries && onChangePumpingIndex && (
        <PasteDailySeries
          title={t("infoPopover.pumping")}
          showTitle={false}
          series={pumpingSeries ?? null}
          columnName={initial.name}
          colIndex={pumpingCol ?? 0}
          scale={scale ?? "daily"}
          scaleLocked={scaleLocked}
          onSeriesChange={onChangePumpingSeries}
          onIndexChange={onChangePumpingIndex}
          onDeleteSeries={onDeleteSeries}
        />
      )}

      <div className="dialog-actions">
        <button
          className="button button-primary"
          onClick={() => {
            try {
              onSave({
                name: name,
                vo: Number(vo),
                alfa: Number(alfa),
                outletCode: outletCode ? Number(outletCode) : null,
              });
            } catch (err) {
              setError(translateError(err, t));
            }
          }}
        >
          {t("common.save")}
        </button>
        <button className="button" onClick={onClose}>
          {t("common.cancel")}
        </button>
      </div>
    </Dialog>
  );
}

/** Just the "add a new aquifer" form — split out of AquifersSection (Step3Outlets.tsx) so the editor tab (AquifersPanel.tsx) can place it above "Draw"/"Link GIS layer", while the wizard keeps it right under its own title. */
export function AquifersForm({
  project,
  onChange,
}: {
  project: EvalHidProject;
  onChange: (project: EvalHidProject) => void;
}) {
  const [aquiferName, setAquiferName] = useState("");
  const [vo, setVo] = useState("");
  const [alfa, setAlfa] = useState("");
  const [outletCode, setOutletCode] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  function handleAddAquifer() {
    setError(null);
    try {
      onChange(
        addAquifer(
          project,
          aquiferName,
          Number(vo),
          Number(alfa),
          outletCode ? Number(outletCode) : null
        )
      );
      setAquiferName("");
      setVo("");
      setAlfa("");
      setOutletCode("");
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <>
      <div className="step-form">
        <input
          placeholder={t("common.name")}
          value={aquiferName}
          onChange={(e) => setAquiferName(e.target.value)}
        />
        <input placeholder="Vo" type="number" value={vo} onChange={(e) => setVo(e.target.value)} />
        <input
          placeholder="Alfa"
          type="number"
          value={alfa}
          onChange={(e) => setAlfa(e.target.value)}
        />
        <select value={outletCode} onChange={(e) => setOutletCode(e.target.value)}>
          <option value="">{t("common.outletPointPlaceholder")}</option>
          {project.outletPoints.map((p) => (
            <option key={p.outletCode} value={p.outletCode}>
              {p.name}
            </option>
          ))}
        </select>
        <button
          className="button"
          onClick={handleAddAquifer}
          disabled={!aquiferName.trim() || !vo || !alfa}
        >
          {t("common.add")}
        </button>
      </div>
      {!outletCode && (aquiferName || vo || alfa) && (
        <p className="engine-notice">{t("step3.noOutletPointWarning")}</p>
      )}
      {error && <p className="error">{error}</p>}
    </>
  );
}

/**
 * The aquifer catalog table (edit/delete) — the other half of
 * AquifersSection (Step3Outlets.tsx), see AquifersForm.
 *
 * `inline`: wizard-only — same reasoning as OutletPointsTable's own
 * `inline` (OutletPointCatalog.tsx), extended to all 4 fields
 * EditAquiferDialog would otherwise edit (name/Vo/Alfa/outlet point), since
 * all 4 are simple values with no series tab in the wizard (series is never
 * passed there — see the prop's own comment).
 */
export function AquifersTable({
  project,
  onChange,
  series,
  onChangeSeries,
  inline = false,
}: {
  project: EvalHidProject;
  onChange: (project: EvalHidProject) => void;
  /** Pasting/viewing the Pumping series is only offered if both are passed (not in the creation wizard, see EditAquiferDialog). */
  series?: ProjectSeries;
  onChangeSeries?: (series: ProjectSeries) => void;
  inline?: boolean;
}) {
  const [editingAquifer, setEditingAquifer] = useState<number | null>(null);
  const [deletingAquifer, setDeletingAquifer] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  const editingAquiferObj = project.aquifers.find((a) => a.aquiferCode === editingAquifer);
  const deletingAquiferObj = project.aquifers.find((a) => a.aquiferCode === deletingAquifer);

  function updateInline(
    a: (typeof project.aquifers)[number],
    changes: Partial<{ name: string; vo: number; alfa: number; outletCode: number | null }>
  ) {
    setError(null);
    try {
      onChange(
        updateAquifer(project, a.aquiferCode, {
          name: changes.name ?? a.name,
          vo: changes.vo ?? a.vo ?? 0,
          alfa: changes.alfa ?? a.alfa ?? 0,
          outletCode: "outletCode" in changes ? (changes.outletCode ?? null) : a.outletCode,
        })
      );
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <>
      {project.aquifers.length > 0 && (
        <div className="table-scroll catalog-table-scroll catalog-table-scroll-capped">
          <table>
            <thead>
              <tr>
                <th>{t("common.name")}</th>
                <th>Vo</th>
                <th>Alfa</th>
                {inline && <th>{t("infoPopover.outletPoints")}</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {project.aquifers.map((a) => (
                <tr
                  key={a.aquiferCode}
                  onClick={inline ? undefined : () => setEditingAquifer(a.aquiferCode)}
                >
                  <td>
                    {inline ? (
                      <input
                        defaultValue={a.name}
                        onBlur={(e) =>
                          e.target.value.trim() && updateInline(a, { name: e.target.value })
                        }
                      />
                    ) : (
                      <>
                        {a.name}
                        {a.outletCode == null && t("step3.noOutletPointSuffix")}
                      </>
                    )}
                  </td>
                  <td>
                    {inline ? (
                      <input
                        className="table-input-narrow"
                        type="number"
                        defaultValue={a.vo ?? ""}
                        onBlur={(e) => updateInline(a, { vo: Number(e.target.value) })}
                      />
                    ) : (
                      (a.vo ?? "")
                    )}
                  </td>
                  <td>
                    {inline ? (
                      <input
                        className="table-input-narrow"
                        type="number"
                        defaultValue={a.alfa ?? ""}
                        onBlur={(e) => updateInline(a, { alfa: Number(e.target.value) })}
                      />
                    ) : (
                      (a.alfa ?? "")
                    )}
                  </td>
                  {inline && (
                    <td>
                      <select
                        value={a.outletCode ?? ""}
                        onChange={(e) =>
                          updateInline(a, {
                            outletCode: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      >
                        <option value="">{t("common.outletPointPlaceholder")}</option>
                        {project.outletPoints.map((p) => (
                          <option key={p.outletCode} value={p.outletCode}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td>
                    <button
                      className="button-icon button-danger"
                      title={t("step3.deleteAquiferPermanent")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingAquifer(a.aquiferCode);
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {inline && error && <p className="error">{error}</p>}
      {editingAquiferObj && (
        <EditAquiferDialog
          initial={{
            name: editingAquiferObj.name,
            vo: editingAquiferObj.vo ?? 0,
            alfa: editingAquiferObj.alfa ?? 0,
            outletCode: editingAquiferObj.outletCode,
          }}
          outletPoints={project.outletPoints}
          pumpingSeries={series?.pumping}
          onChangePumpingSeries={
            onChangeSeries && series
              ? (next) => onChangeSeries({ ...series, pumping: next })
              : undefined
          }
          pumpingCol={editingAquiferObj.pumpingCol}
          scale={project.seriesScale}
          scaleLocked={series ? isSeriesScaleLocked(series) : true}
          onChangePumpingIndex={
            onChangeSeries && series
              ? (index, inferredScale) => {
                  let p = updateAquiferColumn(project, editingAquiferObj.aquiferCode, index);
                  if (inferredScale) p = { ...p, seriesScale: inferredScale };
                  onChange(p);
                }
              : undefined
          }
          onSave={(changes) => {
            onChange(updateAquifer(project, editingAquiferObj.aquiferCode, changes));
            setEditingAquifer(null);
          }}
          onClose={() => setEditingAquifer(null)}
        />
      )}
      {deletingAquiferObj && (
        <ConfirmDeleteDialog
          title={t("step3.deleteAquiferDialogTitle")}
          name={deletingAquiferObj.name}
          onConfirm={() => onChange(deleteAquifer(project, deletingAquiferObj.aquiferCode))}
          onClose={() => setDeletingAquifer(null)}
        />
      )}
    </>
  );
}
